import {
  Box,
  Typography,
  Paper,
  IconButton,
  useMediaQuery,
  useTheme,
  alpha,
  CircularProgress,
} from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { useState, useEffect, useRef, useMemo } from 'react';
import { Document, Page } from 'react-pdf';
import { pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import useLog from 'hooks/useLog';

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).toString();

interface PublishedPdfViewerProps {
  pdfUrl: string;
  mode?: 'single-page' | 'all-pages';
}

const PublishedPdfViewer = ({
  pdfUrl,
  mode = 'single-page',
}: PublishedPdfViewerProps) => {
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const currentPdfUrl = useRef<string>(pdfUrl);
  const { logError } = useLog('PublishedPdfViewer');

  // Memoize the options object to prevent unnecessary reloads
  const documentOptions = useMemo(
    () => ({
      cMapUrl: '/cmaps/',
    }),
    [],
  );

  // Reset state when pdfUrl changes
  useEffect(() => {
    if (currentPdfUrl.current !== pdfUrl) {
      setNumPages(null);
      setPageNumber(1);
      setIsLoading(true);
      setError(null);
      currentPdfUrl.current = pdfUrl;
    }
  }, [pdfUrl]);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setPageNumber(1);
    setIsLoading(false);
    setError(null);
  };

  const onDocumentLoadError = (error: Error) => {
    logError(error, 'onDocumentLoadError');
    setIsLoading(false);
    setError(error.message || 'Failed to load PDF document');
  };

  const previousPage = () =>
    setPageNumber((prevPageNumber) => prevPageNumber - 1);

  const nextPage = () => setPageNumber((prevPageNumber) => prevPageNumber + 1);

  const loadingStatePdf = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        p: 4,
        gap: 2,
        minHeight: isMobile ? 400 : 600,
      }}
    >
      <CircularProgress />
      <Typography variant="body2" color="text.secondary">
        Loading PDF...
      </Typography>
    </Box>
  );

  const loadingStatePage = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        p: 4,
        gap: 2,
        minHeight: isMobile ? 400 : 600,
      }}
    >
      <CircularProgress />
      <Typography variant="body2" color="text.secondary">
        Loading page...
      </Typography>
    </Box>
  );

  const errorDisplay = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        p: 4,
        gap: 2,
        minHeight: isMobile ? 400 : 600,
      }}
    >
      <Typography variant="h6" color="error" textAlign="center">
        Failed to load PDF
      </Typography>
      <Typography variant="body2" color="text.secondary" textAlign="center">
        {error}
      </Typography>
    </Box>
  );

  if (error != null) {
    return errorDisplay;
  }

  if (mode === 'all-pages') {
    return (
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
          px: isMobile ? 1 : 2,
          pb: 4,
        }}
      >
        <Document
          key={pdfUrl}
          file={pdfUrl}
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadError={onDocumentLoadError}
          loading={loadingStatePdf}
          error={errorDisplay}
          options={documentOptions}
        >
          {numPages != null &&
            !isLoading &&
            Array.from(new Array(numPages), (_el, index) => (
              <Paper
                key={`page_${index + 1}`}
                elevation={2}
                sx={{
                  display: 'block',
                  width: '100%',
                  maxWidth: isMobile ? '100%' : '800px',
                  mb: 2,
                }}
              >
                <Page
                  pageNumber={index + 1}
                  width={
                    isMobile
                      ? Math.min(window.innerWidth - 32, 600)
                      : Math.min(800, window.innerWidth - 64)
                  }
                  loading={loadingStatePage}
                  error={
                    <Typography color="error" textAlign="center" p={2}>
                      Failed to load page {index + 1}
                    </Typography>
                  }
                />
              </Paper>
            ))}
        </Document>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2,
        p: 1,
      }}
    >
      {/* Navigation Controls */}
      <Paper
        elevation={2}
        sx={{
          p: isMobile ? 1 : 2,
          display: 'flex',
          alignItems: 'center',
          gap: isMobile ? 1 : 2,
          borderRadius: 2,
        }}
      >
        <IconButton
          onClick={previousPage}
          disabled={pageNumber <= 1 || isLoading}
          color="primary"
          size={isMobile ? 'medium' : 'large'}
          sx={{
            borderRadius: 1,
          }}
        >
          <ChevronLeft />
        </IconButton>

        <Typography
          variant={isMobile ? 'body2' : 'body1'}
          sx={{
            minWidth: isMobile ? 80 : 120,
            textAlign: 'center',
            fontSize: {
              xs: 13,
              md: 15,
            },
          }}
        >
          Page {pageNumber || (numPages != null ? 1 : '--')} of{' '}
          {numPages ?? '--'}
        </Typography>

        <IconButton
          onClick={nextPage}
          disabled={pageNumber >= (numPages ?? 0) || isLoading}
          color="primary"
          size={isMobile ? 'medium' : 'large'}
          sx={{
            borderRadius: 1,
          }}
        >
          <ChevronRight />
        </IconButton>
      </Paper>

      {/* PDF Document Container */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-start',
          overflow: 'auto',
          bgcolor: alpha(theme.palette.primary.main, 0.25),
          borderRadius: 1,
          p: 1,
        }}
      >
        <Paper
          elevation={2}
          sx={{
            display: 'inline-block',
            maxWidth: '100%',
          }}
        >
          <Document
            key={pdfUrl}
            file={pdfUrl}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={onDocumentLoadError}
            loading={loadingStatePdf}
            error={errorDisplay}
            options={documentOptions}
          >
            <Page
              pageNumber={pageNumber}
              width={
                isMobile ? Math.min(window.innerWidth - 32, 600) : undefined
              }
              loading={loadingStatePage}
              error={
                <Typography color="error" textAlign="center" p={2}>
                  Failed to load page {pageNumber}
                </Typography>
              }
            />
          </Document>
        </Paper>
      </Box>
    </Box>
  );
};

export default PublishedPdfViewer;
